import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "@helpers/translate";
import ChevronLeftIcon from "@components/library/Icons/ChevronLeftIcon";
import ChevronRightIcon from "@components/library/Icons/ChevronRightIcon";
import { testimonialsData } from "./testimonialsData";
import "./Testimonials.sass";

const Testimonials = () => {
  const { __ } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [animatingIndex, setAnimatingIndex] = useState(null);
  const [animationDirection, setAnimationDirection] = useState("forward");
  const prevActiveIndex = useRef(activeIndex);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const testimonials = useMemo(() => {
    return testimonialsData.map((item, index) => ({
      ...item,
      quote: __(`main.testimonials.quote${index + 1}`),
      experience: __(`main.testimonials.experience${index + 1}`),
    }));
  }, [__]);

  const totalSlides = testimonials.length;

  useEffect(() => {
    const prevIndex = prevActiveIndex.current;
    const nextIndex = (prevIndex + 1) % totalSlides;
    const prevIndexBackward = (prevIndex - 1 + totalSlides) % totalSlides;

    if (activeIndex === nextIndex && activeIndex !== prevIndex) {
      setAnimationDirection("forward");
      setAnimatingIndex(activeIndex);
      const timer = setTimeout(() => {
        setAnimatingIndex(null);
      }, 1400);

      prevActiveIndex.current = activeIndex;
      return () => clearTimeout(timer);
    } else if (activeIndex === prevIndexBackward && activeIndex !== prevIndex) {
      setAnimationDirection("backward");
      setAnimatingIndex(prevIndex);
      const timer = setTimeout(() => {
        setAnimatingIndex(null);
      }, 1400);

      prevActiveIndex.current = activeIndex;
      return () => clearTimeout(timer);
    } else {
      setAnimatingIndex(null);
      prevActiveIndex.current = activeIndex;
    }
  }, [activeIndex, totalSlides]);

  const goNext = () => {
    setActiveIndex((prevIndex) => (prevIndex + 1) % totalSlides);
  };

  const goPrev = () => {
    setActiveIndex((prevIndex) => (prevIndex - 1 + totalSlides) % totalSlides);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (distance > minSwipeDistance) {
      goNext();
    }

    if (distance < -minSwipeDistance) {
      goPrev();
    }

    touchStartX.current = 0;
    touchEndX.current = 0;
  };
  const getCardClassName = (index) => {
    let diff = index - activeIndex;

    if (diff < 0) {
      diff = diff + totalSlides;
    }
    if (diff >= totalSlides) {
      diff = diff - totalSlides;
    }

    const isAnimatingForward =
      animatingIndex === index &&
      diff === 0 &&
      animationDirection === "forward";
    const isAnimatingBackward =
      animatingIndex === index && animationDirection === "backward";
    const baseClass = "testimonial-card";
    const animatingForwardClass = isAnimatingForward
      ? " testimonial-card--animating"
      : "";
    const animatingBackwardClass = isAnimatingBackward
      ? " testimonial-card--animating-backward"
      : "";

    if (isAnimatingBackward) {
      return `${baseClass} testimonial-card--prev-1${animatingBackwardClass}`;
    }

    if (diff === 0) {
      return `${baseClass} testimonial-card--active${animatingForwardClass}`;
    }

    if (diff === 1) {
      return `${baseClass} testimonial-card--next-1`;
    }

    if (diff === 2) {
      return `${baseClass} testimonial-card--next-2`;
    }

    if (diff === totalSlides - 1) {
      return `${baseClass} testimonial-card--prev-1`;
    }

    return `${baseClass} testimonial-card--next-3`;
  };

  return (
    <section id="testimonials" className="testimonials padding-container">
      <h1 className="testimonials__title text-shark">
        {__("main.testimonials.title")}
      </h1>

      <div
        className="testimonials__carousel"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {testimonials.map((item, index) => (
          <div key={item.nameKey} className={getCardClassName(index)}>
            <blockquote className="testimonial-card__quote text-shark">
              {item.quote}
            </blockquote>

            <div className="testimonial-card__author">
              <img
                src={item.avatar}
                alt={__(item.nameKey)}
                className="testimonial-card__avatar"
              />

              <div className="testimonial-card__info">
                <span className="testimonial-card__name text-shark">
                  {__(item.nameKey)}
                </span>

                <p className="testimonial-card__experience text-storm-gray">
                  {item.experience}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="testimonials__controls">
        <button onClick={goPrev} className="testimonials__button">
          <ChevronLeftIcon />
        </button>
        <button onClick={goNext} className="testimonials__button">
          <ChevronRightIcon />
        </button>
      </div>
    </section>
  );
};

export default Testimonials;
